import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, DailyRewardInfo, Character } from '../contexts/AuthContext';

interface GameDailyRewardsPanelProps {
  onClose: () => void;
  currentCharacterId: string;
  characters: Character[];
}

export const GameDailyRewardsPanel: React.FC<GameDailyRewardsPanelProps> = ({
  onClose,
  currentCharacterId,
  characters,
}): React.ReactElement => {
  const auth = useAuth();
  const [rewardInfo, setRewardInfo] = useState<DailyRewardInfo | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isClaiming, setIsClaiming] = useState<boolean>(false);
  const [claimResult, setClaimResult] = useState<{ success: boolean; pieces: number; bonusItem?: string } | null>(null);
  const [selectedCharacterId, setSelectedCharacterId] = useState<string>(currentCharacterId);

  const loadRewardInfo = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    const info = await auth.getDailyRewardInfo();
    setRewardInfo(info);
    setIsLoading(false);
  }, [auth]);

  useEffect(() => {
    loadRewardInfo();
  }, [loadRewardInfo]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleClaim = async (): Promise<void> => {
    setIsClaiming(true);
    const result = await auth.claimDailyReward();

    if (result.success) {
      setClaimResult({ success: true, pieces: result.piecesEarned, bonusItem: result.bonusItem });
      loadRewardInfo();
    } else {
      setClaimResult({ success: false, pieces: 0 });
    }

    setIsClaiming(false);
  };

  const getStreakDayLabel = (day: number): string => {
    if (day === 7) return 'DAY 7 [BONUS]';
    return `DAY ${day}`;
  };

  const getBonusItemName = (itemId?: string): string => {
    if (!itemId) return '';
    const names: Record<string, string> = {
      'treasure_map': 'Treasure Map',
      'mega_repair_kit': 'Mega Repair Kit',
      'large_health_potion': 'Large Health Potion',
    };
    return names[itemId] || itemId;
  };

  const selectedCharacter = characters.find(c => c.id === selectedCharacterId);

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        background: '#0d1117',
        border: '2px solid #30363d',
        padding: '24px',
        maxWidth: '520px',
        width: '90%',
        fontFamily: 'var(--font-mono)',
        maxHeight: '90vh',
        overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '16px', color: '#f0883e', fontFamily: 'var(--font-mono)' }}>
            DAILY REWARDS
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: '1px solid #f85149',
              color: '#f85149',
              fontSize: '14px',
              cursor: 'pointer',
              padding: '4px 8px',
              fontFamily: 'var(--font-mono), monospace',
              fontWeight: 'bold',
            }}
          >
            x
          </button>
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#8b949e' }}>
            Loading...
          </div>
        ) : rewardInfo ? (
          <>
            {/* Character Selection */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '11px', color: '#8b949e', marginBottom: '8px', textTransform: 'uppercase' }}>
                Claim Reward For
              </div>
              <select
                value={selectedCharacterId}
                onChange={(e) => setSelectedCharacterId(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  background: '#161b22',
                  border: '1px solid #21262d',
                  color: '#e2e8f0',
                  fontFamily: 'var(--font-mono)',
                  fontSize: '12px',
                  cursor: 'pointer',
                }}
              >
                {characters.map(char => (
                  <option key={char.id} value={char.id}>
                    {char.name} (Lvl {char.level})
                  </option>
                ))}
              </select>
              {selectedCharacter && (
                <div style={{ fontSize: '11px', color: '#8b949e', marginTop: '6px' }}>
                  Current Berries: {selectedCharacter.berries.toLocaleString()}₱
                </div>
              )}
            </div>

            {/* Current Streak Display */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '20px',
              gap: '12px',
            }}>
              <div style={{
                flex: 1,
                background: '#161b22',
                border: '1px solid #21262d',
                padding: '16px',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#f0883e' }}>
                  {rewardInfo.currentStreak}
                </div>
                <div style={{ fontSize: '10px', color: '#8b949e', textTransform: 'uppercase' }}>
                  Current Streak
                </div>
              </div>
              <div style={{
                flex: 1,
                background: '#161b22',
                border: '1px solid #21262d',
                padding: '16px',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#58a6ff' }}>
                  {rewardInfo.longestStreak}
                </div>
                <div style={{ fontSize: '10px', color: '#8b949e', textTransform: 'uppercase' }}>
                  Best Streak
                </div>
              </div>
            </div>

            {/* Today's Reward - Claimable */}
            {rewardInfo.canClaimToday && !claimResult?.success && (
              <div style={{
                background: 'linear-gradient(135deg, #1a1a2e 0%, #0d1117 100%)',
                border: '2px solid #f0883e',
                padding: '20px',
                marginBottom: '16px',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '11px', color: '#f0883e', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  {getStreakDayLabel((rewardInfo.currentStreak % 7) + 1)}
                </div>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#fff', marginBottom: '4px' }}>
                  {rewardInfo.todayReward.pieces.toLocaleString()}₱
                </div>
                {rewardInfo.todayReward.bonusItem && (
                  <div style={{ fontSize: '12px', color: '#10b981', marginBottom: '8px' }}>
                    + {getBonusItemName(rewardInfo.todayReward.bonusItem)}
                  </div>
                )}
                <button
                  onClick={handleClaim}
                  disabled={isClaiming}
                  style={{
                    marginTop: '12px',
                    padding: '12px 32px',
                    background: '#f0883e',
                    border: 'none',
                    color: '#000',
                    cursor: isClaiming ? 'wait' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    opacity: isClaiming ? 0.7 : 1,
                  }}
                >
                  {isClaiming ? 'CLAIMING...' : 'CLAIM REWARD'}
                </button>
              </div>
            )}

            {/* Already Claimed Today */}
            {!rewardInfo.canClaimToday && !claimResult?.success && (
              <div style={{
                background: '#161b22',
                border: '1px solid #21262d',
                padding: '20px',
                marginBottom: '16px',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '14px', color: '#10b981', marginBottom: '8px' }}>
                  Reward Claimed Today
                </div>
                <div style={{ fontSize: '11px', color: '#8b949e' }}>
                  Come back tomorrow to continue your streak!
                </div>
              </div>
            )}

            {/* Claim Success Message */}
            {claimResult?.success && (
              <div style={{
                background: '#064e3b',
                border: '2px solid #10b981',
                padding: '20px',
                marginBottom: '16px',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '14px', color: '#10b981', marginBottom: '8px' }}>
                  Reward Claimed!
                </div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#fff' }}>
                  +{claimResult.pieces.toLocaleString()}₱
                </div>
                {claimResult.bonusItem && (
                  <div style={{ fontSize: '12px', color: '#34d399', marginTop: '4px' }}>
                    + {getBonusItemName(claimResult.bonusItem)}
                  </div>
                )}
                {selectedCharacter && (
                  <div style={{ fontSize: '11px', color: '#8b949e', marginTop: '8px' }}>
                    Added to {selectedCharacter.name}
                  </div>
                )}
              </div>
            )}

            {/* Upcoming Rewards Preview */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '11px', color: '#8b949e', marginBottom: '8px', textTransform: 'uppercase' }}>
                Upcoming Rewards
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(6, 1fr)',
                gap: '6px',
              }}>
                {rewardInfo.nextRewards.slice(0, 6).map((reward, index) => (
                  <div
                    key={index}
                    style={{
                      background: reward.bonusItem ? '#1a1a2e' : '#161b22',
                      border: `1px solid ${reward.bonusItem ? '#f0883e' : '#21262d'}`,
                      padding: '8px 4px',
                      textAlign: 'center',
                    }}
                  >
                    <div style={{ fontSize: '9px', color: '#8b949e', marginBottom: '4px' }}>
                      D{reward.day}
                    </div>
                    <div style={{ fontSize: '11px', fontWeight: 'bold', color: reward.bonusItem ? '#f0883e' : '#e2e8f0' }}>
                      {reward.pieces}
                    </div>
                    {reward.bonusItem && (
                      <div style={{ fontSize: '8px', color: '#10b981', marginTop: '2px' }}>
                        +ITEM
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Stats */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '11px',
              color: '#8b949e',
              borderTop: '1px solid #21262d',
              paddingTop: '12px',
            }}>
              <span>Total Claims: {rewardInfo.totalClaims}</span>
              <span>Total Earned: {rewardInfo.totalPiecesEarned.toLocaleString()}₱</span>
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px', color: '#f85149' }}>
            Failed to load rewards
          </div>
        )}
      </div>
    </div>
  );
};
